import React, { FunctionComponent, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { Button, Card } from 'rs-emd-ui-atoms';
import { CompareAlternativesProductHeader } from '.';
import { getLabel } from '../../helpers/html.utils';
import { StockStatus } from '../../models/common/stock.model';
import styles from './compare-alternatives-component.module.scss';
import { Action, CompareAlternativesActionType, CompareAlternativesProps, State } from './compare-alternatives.model';

export const CompareAlternatives: FunctionComponent<CompareAlternativesProps> = (props: CompareAlternativesProps) => {
  const [isOutOfViewport, setIsOutOfViewport] = useState(false);

  const [state, dispatch] = useReducer(reducer, {
    quantity:
      props.data.productsArea.filter((p) => p.stockArea?.statusCode !== StockStatus.Discontinued)[0].priceArea?.minimumOrderQuantity ?? 0,
    isValidQty: true,
    isBusy: false,
    prices: [],
  });

  const ref = useRef<HTMLDivElement>(null);

  // datasheets
  useLayoutEffect(() => {
    props.data.productsArea.forEach((p) => {
      props.productService.getDataSheets({ stockCode: p.title.stockCode, documentsType: '1' }).then((data) => {
        dispatch({ type: CompareAlternativesActionType.SetDataSheets, stockCode: p.title.stockCode, data });
      });
    });
  }, [props.data.productsArea, props.productService]);

  // Fixed CTA
  useLayoutEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        setIsOutOfViewport(
          // cannot use window.scrollY since overflow-x: hidden on the body causes it to stop working
          Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop) >
            ref.current.offsetTop + ref.current.clientHeight
        );
      }
    };
    document.body.classList.add('body-compare-alternatives');

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', () => handleScroll);
      document.body.classList.remove('body-compare-alternatives');
    };
  }, []);

  return (
    <div className={`${styles['compare-alternatives']}`}>
      <div className='inner-page-wrapper'>
        <div className={`${styles['fixed-cta']} ${isOutOfViewport ? `${styles.fixed} ${false ? styles.top : ''}` : styles.hide}`}>
          <div className={`${styles['fixed-cta-wrapper']} inner-page-wrapper`}>
            {props.data.productsArea.map((p, i) => {
              return (
                <CompareAlternativesProductHeader
                  labels={props.data.labels ?? []}
                  product={p}
                  inFixedCTA={true}
                  isFirstProduct={i === 0}
                  isLastProduct={i === props.data.productsArea.length - 1}
                />
              );
            })}
          </div>
        </div>
        <div className={styles['lcard-container']}>
          <div className={styles['compare-alternatives-container']}>
            <div className={styles['compare-alternatives-heading']}>
              <h1 className={styles['compare-alternatives-h1']}>{getLabel(props.data.labels, 'title')}</h1>
            </div>
          </div>
        </div>

        <div className={styles['info-container']}>
          <div className={styles['compare-alternatives-product-wrapper']} ref={ref}>
            {props.data.productsArea.map((p) => {
              return <CompareAlternativesProductHeader labels={props.data.labels ?? []} product={p} />;
            })}
          </div>
          <div className={styles['compare-alternatives-product-info-wrapper']}>
            {/* Price */}
            <div className={styles['label']}>
              <p>{getLabel(props.data.labels, 'price')}</p>
            </div>

            <div className={styles['value-container']}>
              {props.data.productsArea?.map((p) => {
                return <Card className={styles['value']}>{p.priceArea?.price.priceAfter?.displayValue}</Card>;
              })}
            </div>

            {/* StockCode */}
            <div className={styles['label']}>
              <p>{getLabel(props.data.labels, 'stock_code')}</p>
            </div>

            <div className={styles['value-container']}>
              {props.data.productsArea?.map((p) => {
                return <Card className={styles['value']}>{p.title.stockCode}</Card>;
              })}
            </div>

            {/* MPN */}
            {props.data.productsArea.some((p) => p.title.mpn) && (
              <>
                <div className={styles['label']}>
                  <p>{getLabel(props.data.labels, 'mpn')}</p>
                </div>

                <div className={styles['value-container']}>
                  {props.data.productsArea?.map((p) => {
                    return <Card className={styles['value']}>{p.title.mpn}</Card>;
                  })}
                </div>
              </>
            )}

            {/* HSN */}
            {props.data.productsArea.some((p) => p.title.hsnCode) && (
              <>
                <div className={styles['label']}>
                  <p>{getLabel(props.data.labels, 'hsn')}</p>
                </div>

                <div className={styles['value-container']}>
                  {props.data.productsArea?.map((p) => {
                    return <Card className={styles['value']}>{p.title.hsnCode}</Card>;
                  })}
                </div>
              </>
            )}

            {/* Price */}
            <div className={styles['label']}>
              <p>{getLabel(props.data.labels, 'brand')}</p>
            </div>

            <div className={styles['value-container']}>
              {props.data.productsArea?.map((p) => {
                return <Card className={styles['value']}>{p.title.brand}</Card>;
              })}
            </div>

            {/* Datasheet */}
            {state.dataSheets && (
              <>
                <div className={styles['label']}>
                  <p>{getLabel(props.data.labels, 'technical_documents')}</p>
                </div>
                <div className={styles['value-container']}>
                  {props.data.productsArea?.map((p) => {
                    return (
                      <Card className={`${styles.value} ${styles.documents}`}>
                        {state.dataSheets &&
                          state.dataSheets[p.title.stockCode]?.map((d) => {
                            return <Button icon='pdf' buttonType='text' text={d.title} href={d.url} isExternalLink={true}></Button>;
                          })}
                      </Card>
                    );
                  })}
                </div>
              </>
            )}

            {/* Attributes */}
            {props.data.productsArea[0].attributes?.map((a) => {
              return (
                <>
                  <div className={styles['label']}>
                    <p>{a.name}</p>
                  </div>
                  <div className={styles['value-container']}>
                    {props.data.productsArea?.map((p) => {
                      return <Card className={styles['value']}>{p.attributes?.find((at) => at.name === a.name)?.value ?? '-'}</Card>;
                    })}
                  </div>
                </>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case CompareAlternativesActionType.SetIsValidQty:
        return {
          ...state,
          isValidQty: action.isValid,
          quantity: action.quantity,
        };
      case CompareAlternativesActionType.IsBusy: {
        return {
          ...state,
          isBusy: action.isBusy,
        };
      }
      case CompareAlternativesActionType.SetDataSheets: {
        let dataSheets = state.dataSheets;
        if (action.data.dataSheets) {
          dataSheets = dataSheets ?? {};
          dataSheets[action.stockCode] = action.data.dataSheets;
        }
        return { ...state, dataSheets: dataSheets };
      }
      case CompareAlternativesActionType.SetPriceData: {
        return {
          ...state,
          prices: action.data.productPrices,
        };
      }
    }
  }
};
